import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import apiHelper from '../apiHelper';

// ── Two-box search for Platform Listing / Platform Pricing (ERP-5233) ────────
//
// Replaces the single SearchBox both toolbars used to carry. That box OR-ed one
// term across the stock's own names AND every platform mapping field, so a model
// name and a platform SKU id typed together returned everything matching either —
// there was no way to say "this model, on that listing".
//
//   Left box    the SPU model name, and only that. It has a typeahead of real
//               model names, and picking one pins the search to that exact
//               model: choosing "iPhone 15" means iPhone 15, not iPhone 15 Pro.
//               Typing your own words and pressing Enter stays broad.
//   Right box   everything the old single box searched EXCEPT the model name —
//               ERP sku name and model number, condition, condition type, both
//               remark fields, and the four marketplace columns. No dropdown and
//               no typeahead: it spans a dozen columns, so there is no single
//               list to suggest from, and nothing to choose between.
//
// The two are AND-ed by the backend, and either works alone.

// Matches ListingSearchSuggestionsAPI.MIN_QUERY_LENGTH. Asking below this gets an
// empty list back, so there is no point spending the request.
const MIN_SUGGEST_LENGTH = 2;
const SUGGEST_DEBOUNCE_MS = 300;

// Figma inspect: border 1px solid Utilised/text-2 (#6C757D), height 40, padding 10.
const BOX_BORDER = '1px solid var(--Gray-600, #6C757D)';
const BOX_BORDER_DISABLED = '1px solid var(--Gray-400, #CED4DA)';
// Focus is the brand yellow the rest of the app already uses for it (#FFEB00,
// ~340 occurrences); #FFFEF0 is its hover/selected wash (~80).
const BOX_BORDER_FOCUS = '1px solid #FFEB00';
// The label sits on Utilised/neutral-200, which is what separates it from the
// white input beside it.
const FIELD_PREFIX_BG = 'var(--Gray-100, #F8F9FA)';

// Figma export: min-width 260, flex 1, height 40, 1px #6C757D, radius 8, and
// overflow:hidden — which is what squares off the label's own corners against
// the rounded box, so the label carries no radius of its own.
const boxStyle = (disabled, focused) => ({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    height: 40,
    minWidth: 260,
    border: disabled ? BOX_BORDER_DISABLED : (focused ? BOX_BORDER_FOCUS : BOX_BORDER),
    background: disabled ? 'var(--Gray-100, #F8F9FA)' : 'var(--Default-White, #FFF)',
    borderRadius: 8,
    overflow: 'hidden',
    cursor: disabled ? 'not-allowed' : 'default',
    fontFamily: 'Montserrat',
});

// Figma inspect, Body/Base text: Montserrat 400, 14px on an 18px line. The size
// stays inline so nothing in company.scss can outrank it; the placeholder colour
// has to live in the stylesheet, because ::placeholder is a pseudo-element and
// cannot be set from a style attribute.
const inputStyle = {
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: 14,
    lineHeight: '18px',
    fontFamily: 'Montserrat',
    color: '#212529',
    flex: 1,
    minWidth: 0,
    padding: '0 10px',
};

// The suggestion list. Absolute so it never reflows the toolbar, and z-index above
// the table's sticky header, which is z 5.
const menuStyle = {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    zIndex: 20,
    background: '#FFFFFF',
    border: '1px solid var(--Gray-300, #DEE2E6)',
    borderRadius: 8,
    overflow: 'hidden',
    maxHeight: 260,
    overflowY: 'auto',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
};

// Figma inspect, menu row: height 40, min-width 140, padding 15px 30px 15px 10px,
// gap 4, background primary-4 (#FFFEF0) on the highlighted one.
//
// The 15px top/bottom is dropped in favour of minHeight: Figma reports a fixed
// 40px frame AND 15px of vertical padding, which in CSS would leave a 10px
// content box and fight the ellipsis clipping below. Centring in a 40px row
// renders what the design shows.
const itemStyle = (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    minHeight: 40,
    minWidth: 140,
    boxSizing: 'border-box',
    padding: '0 30px 0 10px',
    fontSize: 14,
    lineHeight: '18px',
    fontFamily: 'Montserrat',
    color: '#212529',
    cursor: 'pointer',
    background: active ? '#FFFEF0' : 'transparent',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
});

// One box: optional label, input, clear, search icon, and — when `suggest` is on —
// the typeahead menu.
//
// onCommit(term, exact): `exact` is true only when the term came out of the menu.
// It is what tells the backend to match the model name end to end instead of
// loosely, and it is meaningless for a box with no menu, which always sends false.
function SearchField({
    prefix,
    value,
    onValueChange,
    onCommit,
    suggest = false,
    placeholder = 'Search...',
    disabled,
}) {
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [focused, setFocused] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const wrapRef = useRef(null);
    const timerRef = useRef(null);
    // Only the newest request may write state: typing fast enough and the answer
    // for "iPh" can land after the answer for "iPhone" and repopulate the list
    // with stale rows.
    const requestSeq = useRef(0);

    useEffect(() => {
        const onDocMouseDown = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDocMouseDown);
        return () => document.removeEventListener('mousedown', onDocMouseDown);
    }, []);

    useEffect(() => () => clearTimeout(timerRef.current), []);

    const fetchSuggestions = useCallback((term) => {
        const seq = ++requestSeq.current;
        const params = new URLSearchParams({ q: term });
        apiHelper(`sales/listings/search-suggestions/?${params.toString()}`, 'GET')
            .then((r) => (r.ok ? r.json() : { suggestions: [] }))
            .then((data) => {
                if (seq !== requestSeq.current) return;
                const list = data.suggestions || [];
                setSuggestions(list);
                setActiveIndex(-1);
                setOpen(list.length > 0);
            })
            // A failed typeahead must never be louder than the search itself:
            // close the menu and let the user press Enter.
            .catch(() => {
                if (seq === requestSeq.current) { setSuggestions([]); setOpen(false); }
            });
    }, []);

    const commit = (term, exact = false) => {
        clearTimeout(timerRef.current);
        setOpen(false);
        setActiveIndex(-1);
        onCommit(term.trim(), exact);
    };

    const handleChange = (e) => {
        const next = e.target.value;
        onValueChange(next);
        clearTimeout(timerRef.current);
        // Emptying the box clears that half of the search straight away — the
        // same thing the old SearchBox did, and it saves an Enter.
        if (next.trim() === '') {
            setSuggestions([]);
            setOpen(false);
            onCommit('', false);
            return;
        }
        if (!suggest || next.trim().length < MIN_SUGGEST_LENGTH) {
            setSuggestions([]);
            setOpen(false);
            return;
        }
        timerRef.current = setTimeout(() => fetchSuggestions(next.trim()), SUGGEST_DEBOUNCE_MS);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown' && suggestions.length) {
            e.preventDefault();
            setOpen(true);
            setActiveIndex((i) => (i + 1) % suggestions.length);
        } else if (e.key === 'ArrowUp' && suggestions.length) {
            e.preventDefault();
            setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
        } else if (e.key === 'Enter') {
            // Enter on a highlighted suggestion takes it, exactly — same as
            // clicking it. Enter otherwise searches whatever is typed, loosely,
            // which is what people expect from a search box.
            const picked = activeIndex >= 0 ? suggestions[activeIndex] : null;
            if (picked) onValueChange(picked);
            commit(picked || value, Boolean(picked));
        } else if (e.key === 'Escape') {
            setOpen(false);
        }
    };

    const handleClear = () => {
        onValueChange('');
        setSuggestions([]);
        commit('');
    };

    return (
        <div ref={wrapRef} style={{ position: 'relative', flex: 1, minWidth: 0 }}>
            <div style={boxStyle(disabled, focused)}>
                {prefix}
                <input
                    type="text"
                    className="listing-search-input"
                    placeholder={placeholder}
                    value={value}
                    disabled={disabled}
                    onChange={disabled ? undefined : handleChange}
                    onKeyDown={disabled ? undefined : handleKeyDown}
                    onFocus={() => { setFocused(true); if (suggestions.length) setOpen(true); }}
                    onBlur={() => setFocused(false)}
                    style={inputStyle}
                />
                {!disabled && value !== '' && (
                    <button
                        type="button"
                        onClick={handleClear}
                        title="Clear"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex', padding: 0, marginRight: 4 }}
                    >
                        <X size={14} color="#6C757D" />
                    </button>
                )}
                <button
                    type="button"
                    onClick={() => !disabled && commit(value)}
                    disabled={disabled}
                    title="Search"
                    style={{ background: 'none', border: 'none', cursor: disabled ? 'not-allowed' : 'pointer', display: 'inline-flex', padding: '0 10px 0 0', flexShrink: 0 }}
                >
                    <Search size={18} color={disabled ? '#CED4DA' : '#6C757D'} />
                </button>
            </div>

            {open && suggestions.length > 0 && (
                <div style={menuStyle}>
                    {suggestions.map((sug, i) => (
                        <div
                            key={`${sug}-${i}`}
                            style={itemStyle(i === activeIndex)}
                            title={sug}
                            onMouseEnter={() => setActiveIndex(i)}
                            // onMouseDown, not onClick: the input's blur would close the
                            // menu before a click ever landed.
                            onMouseDown={(e) => { e.preventDefault(); onValueChange(sug); commit(sug, true); }}
                        >
                            {sug}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function ListingSearchBars({
    modelValue,
    onModelValueChange,
    onModelCommit,       // (term, exact)
    detailValue,
    onDetailValueChange,
    onDetailCommit,
    disabled = false,
}) {
    // Figma export: padding 10, Montserrat 14 in #6C757D on #F8F9FA, a 1px
    // #6C757D divider on the right, no radius of its own — the box clips it.
    const modelPrefix = (
        <span style={{
            padding: 10, fontSize: 14, color: '#6C757D', fontFamily: 'Montserrat',
            background: FIELD_PREFIX_BG,
            borderRight: disabled ? BOX_BORDER_DISABLED : BOX_BORDER,
            alignSelf: 'stretch', display: 'flex', alignItems: 'center', flexShrink: 0,
        }}>
            SPU
        </span>
    );

    // Both boxes share whatever the toolbar leaves them, in equal halves — the
    // design measures the left box at 1051px on a frame that wide, i.e. it is
    // sized by the row, not by a number. minWidth:0 on every level or a flex item
    // refuses to shrink below its content and the pair overflows the card.
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
            <SearchField
                prefix={modelPrefix}
                value={modelValue}
                onValueChange={onModelValueChange}
                onCommit={onModelCommit}
                suggest
                disabled={disabled}
            />
            <SearchField
                value={detailValue}
                onValueChange={onDetailValueChange}
                onCommit={onDetailCommit}
                // Names what this box reaches, since it has no label of its own:
                // the storage/colour half of the SKU name, the marketplace
                // listing's name and id, and both remark fields.
                placeholder="Search Storage, Color, Listing Name, Listing ID, Notes..."
                disabled={disabled}
            />
        </div>
    );
}
